import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Clock, CheckCircle2, XCircle, Loader2, AlertCircle } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import { engineEndpoints, type PreScreenRecord } from '@/lib/contracts'
import { apiRequest } from '@/lib/apiClient'

// ─── Recommendation pill ─────────────────────────────────────────────────────

function RecommendationPill({ rec }: { rec: PreScreenRecord['ericaRecommendation'] }) {
  if (!rec) return null
  const config = {
    progress: { label: 'Proceed', className: 'bg-green-500/20 text-green-400 border border-green-500/30' },
    hold:     { label: 'Hold',    className: 'bg-[#FD802E]/20 text-[#FD802E] border border-[#FD802E]/30' },
    decline:  { label: 'Decline', className: 'bg-red-500/20 text-red-400 border border-red-500/30' },
  }
  const { label, className } = config[rec]
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${className}`}>
      Erica: {label}
    </span>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

export function PreScreenResults() {
  const { id: projectId, candidateId } = useParams<{ id: string; candidateId: string }>()
  const navigate = useNavigate()

  const [preScreen, setPreScreen] = useState<PreScreenRecord | null>(null)
  const [candidateName, setCandidateName] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [movingToShortlist, setMovingToShortlist] = useState(false)
  const [shortlisted, setShortlisted] = useState(false)

  useEffect(() => {
    if (!projectId || !candidateId) {
      setLoading(false)
      return
    }

    Promise.all([
      engineApiClient.getPreScreen(projectId, candidateId),
      engineApiClient.getCandidateScreening(projectId, candidateId).catch(() => null),
    ]).then(([ps, screening]) => {
      setPreScreen(ps)
      if (screening?.candidateName) setCandidateName(screening.candidateName)
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })
  }, [projectId, candidateId])

  const handleMoveToShortlist = async () => {
    if (!projectId || !candidateId || movingToShortlist || shortlisted) return
    setMovingToShortlist(true)
    try {
      await apiRequest({
        path: engineEndpoints.candidateStage(projectId, candidateId),
        method: 'PATCH',
        body: { stage: 'shortlisted' },
      })
      setShortlisted(true)
    } catch (err) {
      console.error('[PreScreenResults] move to shortlist failed:', err)
    } finally {
      setMovingToShortlist(false)
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#02182B] flex flex-col">
        <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to={`/project/${projectId}/candidate/${candidateId}`} className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-white text-xl font-semibold">Pre-Screen Results</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 size={20} className="animate-spin text-[#FD802E]" />
            <span className="text-sm">Loading pre-screen results…</span>
          </div>
        </div>
      </div>
    )
  }

  // ── API error ─────────────────────────────────────────────────
  if (fetchError) {
    return (
      <div className="min-h-screen bg-[#02182B] flex flex-col">
        <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to={`/project/${projectId}/candidate/${candidateId}`} className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-white text-xl font-semibold">Pre-Screen Results</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md space-y-4">
            <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={24} className="text-red-400" />
            </div>
            <p className="text-red-400 text-sm font-medium">Unable to load pre-screen results. Try refreshing.</p>
            <button
              onClick={() => navigate(`/project/${projectId}/candidate/${candidateId}`)}
              className="bg-[#FD802E] text-[#02182B] font-semibold px-6 py-3 rounded-lg hover:bg-[#e6721a] transition-colors text-sm"
            >
              Go back to Candidate
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Empty / not completed ─────────────────────────────────────────────────
  if (!preScreen || preScreen.status !== 'complete') {
    return (
      <div className="min-h-screen bg-[#02182B] flex flex-col">
        <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to={`/project/${projectId}/candidate/${candidateId}`} className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-white text-xl font-semibold">Pre-Screen Results</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-[#233D4C] rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock size={28} className="text-gray-500" />
            </div>
            <h2 className="text-white text-lg font-semibold mb-2">No pre-screen results yet</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Send the candidate their pre-screen link to get started. Once they complete the conversation with Erica, results will appear here.
            </p>
            <button
              onClick={() => navigate(`/project/${projectId}/candidate/${candidateId}`)}
              className="bg-[#FD802E] text-[#02182B] font-semibold px-6 py-3 rounded-lg hover:bg-[#e6721a] transition-colors text-sm"
            >
              Back to Candidate
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Results ───────────────────────────────────────────────────────────────
  const score = preScreen.postScreenScore
  const completedDate = preScreen.completedAt
    ? new Date(preScreen.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null

  return (
    <div className="min-h-screen bg-[#02182B]">
      {/* Header */}
      <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <Link to={`/project/${projectId}/candidate/${candidateId}`} className="text-gray-400 hover:text-white transition-colors flex-shrink-0">
                <ArrowLeft size={20} />
              </Link>
              <div className="min-w-0">
                <h1 className="text-white text-xl font-semibold truncate">
                  {candidateName || 'Candidate'}
                </h1>
                {completedDate && (
                  <p className="text-gray-400 text-xs mt-0.5">Pre-screen completed {completedDate}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                Pre-Screen Complete
              </span>
              <RecommendationPill rec={preScreen.ericaRecommendation} />
              {score !== null && (
                <span className="px-3 py-1 rounded-full text-sm font-bold bg-[#233D4C] text-white border border-[#2a4a5c]">
                  {score}/100
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* Erica's Assessment */}
        {preScreen.postScreenRationale && (
          <section className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
            <h2 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide text-[#FD802E]">
              Erica's Assessment
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              {preScreen.postScreenRationale}
            </p>
          </section>
        )}

        {/* Must-Have Coverage */}
        {preScreen.mustHaveCoverage.length > 0 && (
          <section className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
            <h2 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide text-[#FD802E]">
              Must-Have Coverage
            </h2>
            <div className="space-y-3">
              {preScreen.mustHaveCoverage.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  {item.confirmed
                    ? <CheckCircle2 size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
                    : <XCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                  }
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">{item.criterion}</p>
                    {item.notes && (
                      <p className="text-gray-400 text-xs mt-0.5">{item.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Q&A Transcript */}
        {preScreen.responses.length > 0 && (
          <section className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
            <h2 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide text-[#FD802E]">
              Q&amp;A Transcript
            </h2>
            <div className="space-y-5">
              {preScreen.responses.map((qa, i) => (
                <div key={i} className="space-y-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Q{i + 1}
                  </p>
                  <p className="text-white text-sm font-medium leading-snug">{qa.question}</p>
                  <div className="bg-[#02182B] border border-[#2a4a5c] rounded-lg p-3">
                    <p className="text-gray-300 text-sm leading-relaxed">{qa.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Actions */}
        <section className="flex items-center gap-3 pt-2 pb-8">
          <button
            onClick={() => navigate(`/project/${projectId}/candidate/${candidateId}`)}
            className="flex-1 px-6 py-3 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors font-medium text-center text-sm border border-[#2a4a5c]"
          >
            Back to Candidate
          </button>
          <button
            onClick={handleMoveToShortlist}
            disabled={movingToShortlist || shortlisted}
            className={`flex-1 px-6 py-3 rounded-lg transition-colors font-semibold text-sm flex items-center justify-center gap-2
              ${shortlisted
                ? 'bg-green-600 text-white cursor-default'
                : 'bg-[#FD802E] text-[#02182B] hover:bg-[#e6721a] disabled:opacity-60 disabled:cursor-not-allowed'
              }`}
          >
            {movingToShortlist && <Loader2 size={14} className="animate-spin" />}
            {shortlisted ? '✓ Moved to Shortlist' : 'Move to Shortlist'}
          </button>
        </section>

      </main>
    </div>
  )
}
